import { Injectable, NgZone } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environment/environment';
import { DigioSdkResponse, DigioSdkOptions } from '../models/digio-interfaces';

@Injectable({
  providedIn: 'root'
})
export class DigioSdkService {
  private sdkLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(private ngZone: NgZone) {}

  /**
   * Dynamically loads the Digio SDK script if not already present in the DOM.
   */
  loadSdk(): Observable<void> {
    if (this.sdkLoaded && (window as any).Digio) {
      return of(void 0);
    }
    if (this.loadingPromise) {
      return from(this.loadingPromise);
    }

    const url = environment.digioSdkUrl || 'https://app.digio.in/sdk/v11/digio.js';
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      // Check if script already in document
      if ((window as any).Digio) {
        this.sdkLoaded = true;
        resolve();
        return;
      }

      const existingScript = document.querySelector(`script[src*="digio.js"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          this.ngZone.run(() => {
            this.sdkLoaded = true;
            resolve();
          });
        });
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.async = true;
      script.onload = () => {
        this.ngZone.run(() => {
          this.sdkLoaded = true;
          resolve();
        });
      };
      script.onerror = (err) => {
        this.ngZone.run(() => {
          console.error('Failed to load Digio SDK script', err);
          this.loadingPromise = null;
          reject(err);
        });
      };
      document.body.appendChild(script);
    });

    return from(this.loadingPromise);
  }

  /**
   * Opens the Digio eSign modal using the official Digio Web SDK.
   * @param documentId Document ID returned by backend (e.g. DID2609...)
   * @param identifier Signer Email or Mobile number
   * @param tokenId Optional access token / signing token
   * @param options Custom SDK options (environment, logo, is_iframe)
   */
  openDigio(
    documentId: string,
    identifier: string,
    tokenId?: string,
    options?: Partial<DigioSdkOptions>
  ): Observable<DigioSdkResponse> {
    return this.loadSdk().pipe(
      switchMap(() => {
        const DigioConstructor = (window as any).Digio;
        if (!DigioConstructor) {
          return of({
            success: false,
            message: 'Digio SDK script is not loaded in window context'
          });
        }

        return new Observable<DigioSdkResponse>((observer) => {
          try {
            // Environment must be 'sandbox' or 'production' in Digio SDK
            const digioEnvironment = options?.environment || (environment.production ? 'production' : 'sandbox');

            const sdkOptions: any = {
              environment: digioEnvironment,
              is_iframe: options?.is_iframe !== undefined ? options.is_iframe : true,
              logo: options?.logo || 'https://nishwikbank.com/assets/logo.png',
              callback: (response: any) => {
                this.ngZone.run(() => {
                  console.log('[Digio SDK] Callback Response:', response);
                  // Digio response format: { doc_id: '...', message: '...', error_code: '...', txn_id: '...' }
                  if (response && (response.message === 'success' || !response.error_code || response.error_code === '0' || response.status === 'success')) {
                    observer.next({
                      success: true,
                      documentId: response.doc_id || documentId,
                      details: response,
                      message: response.message || 'Aadhaar OTP eSign completed successfully'
                    });
                  } else {
                    observer.next({
                      success: false,
                      documentId: response?.doc_id || documentId,
                      errorCode: response?.error_code,
                      message: response?.message || 'Digio signature window closed or not completed',
                      details: response
                    });
                  }
                  observer.complete();
                });
              }
            };

            console.log('[Digio SDK] Initializing Digio instance with options:', sdkOptions);
            const digioInstance = new DigioConstructor(sdkOptions);

            if (typeof digioInstance.init === 'function') {
              digioInstance.init();
            }

            console.log('[Digio SDK] Submitting document:', documentId, 'identifier:', identifier, 'token:', tokenId);

            // Digio submit invocation
            if (typeof digioInstance.submit === 'function') {
              if (tokenId && tokenId.trim().length > 0) {
                digioInstance.submit(documentId, identifier, tokenId);
              } else {
                digioInstance.submit(documentId, identifier);
              }
            } else if (typeof digioInstance.open === 'function') {
              digioInstance.open({
                docId: documentId,
                identifier: identifier,
                tokenId: tokenId || ''
              });
            } else {
              observer.next({
                success: false,
                message: 'Digio submit method not found on SDK instance'
              });
              observer.complete();
            }
          } catch (err: any) {
            console.error('[Digio SDK] Error launching Digio modal:', err);
            this.ngZone.run(() => {
              observer.next({
                success: false,
                message: err?.message || 'Error executing Digio SDK modal'
              });
              observer.complete();
            });
          }
        });
      }),
      catchError((err) => {
        console.error('[Digio SDK] CatchError handler:', err);
        return of({
          success: false,
          message: err?.message || 'Failed to initialize Digio SDK'
        });
      })
    );
  }
}
